import { Box, Skeleton } from "@mui/material";
import React from "react";

import FilterPanel from "../Filters/FilterPanel";
import { ExploreFilterLayout, FILTER_ROW_COLUMN_WIDTH } from "./ExploreFilterPanel";

// How many values each placeholder section shows, one section per entry
const SIDE_SECTIONS = [4, 3, 3, 2];

// How many sections the side panel lays out, the counts above repeating:
// more than the tallest screen shows, the rest cut off at the panel's foot
const SIDE_SECTION_COUNT = 24;

// How many columns the row shows, each with as many values
const ROW_COLUMNS = 8;
const ROW_VALUES = 5;

// A section's name and its values as quiet bars, the values shortening
// down the list
const Bars: React.FC<{ values: number }> = ({ values }) => (
  <>
    {Array.from({ length: values }, (_, value) => (
      <Skeleton
        key={value}
        variant="text"
        width={`${80 - value * 12}%`}
        sx={{ fontSize: "0.85rem" }}
      />
    ))}
  </>
);

export interface ExploreFilterPanelPlaceholderProps {
  // As the panel it stands in for: a column beside the results, or a row
  layout?: ExploreFilterLayout;
  // The number of active filters, which the side panel's head shows
  activeCount?: number;
}

// The filter panel while the first answer is on its way, laid out as the
// panel will be, the sections as quiet bars, so that the space keeps its
// shape. It is only a picture, and hidden from assistive technology.
const ExploreFilterPanelPlaceholder: React.FC<
  ExploreFilterPanelPlaceholderProps
> = ({ layout = "side", activeCount = 0 }) =>
  layout === "side" ? (
    // As tall as the column it stands in, down to the bottom of the page,
    // and out of its flow, so that the sections cut off never stretch it
    <Box
      aria-hidden
      sx={{
        position: "absolute",
        inset: 0,
        display: "flex",
        flexDirection: "column",
        "& > aside": { flex: 1, minHeight: 0 },
      }}
    >
      <FilterPanel activeCount={activeCount}>
        {Array.from({ length: SIDE_SECTION_COUNT }, (_, section) => (
          <Box key={section} sx={{ px: 1.5, pt: 1.5 }}>
            <Skeleton variant="text" width="45%" sx={{ fontSize: "0.72rem" }} />
            <Bars values={SIDE_SECTIONS[section % SIDE_SECTIONS.length]} />
          </Box>
        ))}
      </FilterPanel>
    </Box>
  ) : (
    <Box
      aria-hidden
      sx={{
        display: "grid",
        gridTemplateColumns: `repeat(auto-fill, minmax(${FILTER_ROW_COLUMN_WIDTH}px, 1fr))`,
        columnGap: 1.5,
        // One row of columns, as many as fit: the rest fall into rows of no
        // height, cut off with no gap above them
        gridTemplateRows: "auto",
        gridAutoRows: 0,
        overflow: "hidden",
      }}
    >
      {Array.from({ length: ROW_COLUMNS }, (_, column) => (
        <Box
          key={column}
          sx={{
            border: 1,
            borderColor: "divider",
            borderRadius: (theme) => `${theme.radius.surface}px`,
            backgroundColor: "background.paper",
          }}
        >
          <Box
            sx={{ px: 1.5, py: 1, borderBottom: 1, borderColor: "divider" }}
          >
            <Skeleton variant="text" width="50%" sx={{ fontSize: "0.72rem" }} />
          </Box>
          <Box sx={{ px: 1.5, py: 1 }}>
            <Bars values={ROW_VALUES} />
          </Box>
        </Box>
      ))}
    </Box>
  );

export default ExploreFilterPanelPlaceholder;
